import type { QueryClient, UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import { invalidateSolarFinancingLockReaders } from "./solar-financing-lock-readers"
import { invalidateSolarPriceDependents } from "./solar-price-dependents"
import type { SolarLightReachDto } from "../dto/solar-lightreach.dto"

/**
 * What a LightReach command moves beyond its own read.
 *
 * - `pricing`: Get LightReach pricing and choosing an offer write the offers,
 *   the Quote and the back-solved margin (one PowurOS price), so every read
 *   of the price, the payment, savings, the contract, Share and the Summary's
 *   `lightReachOffer` requirement refetches.
 * - `application` and `contract`: a held or approved application, and a sent
 *   contract, lock the selections, which every read behind the financing lock
 *   reports, and the contract readiness reads approval and the main contract.
 */
export type SolarLightReachCommandScope = `application` | `contract` | `pricing`

const invalidateDependents = (
  queryClient: QueryClient,
  proposalId: string,
  scope: SolarLightReachCommandScope,
): void => {
  if (scope === `pricing`) {
    invalidateSolarPriceDependents(queryClient, proposalId, {
      except: [`lightReach`],
    })
  } else {
    invalidateSolarFinancingLockReaders(queryClient, proposalId)
    for (const queryKey of [
      queryKeys.solarProposal.pricing(proposalId),
      queryKeys.solarProposal.savings(proposalId),
      queryKeys.solarProposal.share(proposalId),
    ]) {
      void queryClient.invalidateQueries({ queryKey, exact: true })
    }
  }
  void queryClient.invalidateQueries({
    queryKey: queryKeys.solarProposal.summary(proposalId),
    exact: true,
  })
}

/** Every LightReach command answers with the whole LightReach read. Not awaited. */
export const applySolarLightReachRead = (
  queryClient: QueryClient,
  proposalId: string,
  read: SolarLightReachDto,
  scope: SolarLightReachCommandScope,
): void => {
  const lightReachKey = queryKeys.solarProposal.lightReach(proposalId)
  // A read already in flight saw the state before the command.
  void queryClient.cancelQueries({ queryKey: lightReachKey, exact: true })
  queryClient.setQueryData(lightReachKey, read)
  invalidateDependents(queryClient, proposalId, scope)
}

/**
 * A refused or failed command may still have moved LightReach (a held
 * submission, newer offers, a contract LightReach accepted): read it all
 * again. Not awaited.
 */
export const invalidateSolarLightReachDependents = (
  queryClient: QueryClient,
  proposalId: string,
  scope: SolarLightReachCommandScope,
): void => {
  void queryClient.invalidateQueries({
    queryKey: queryKeys.solarProposal.lightReach(proposalId),
    exact: true,
  })
  invalidateDependents(queryClient, proposalId, scope)
}

/**
 * A LightReach command: it answers with the whole LightReach read, and a
 * failure reads LightReach and the scope's dependents again. `cacheTime`
 * passes through for commands whose variables must not outlive the call.
 */
export const useSolarLightReachCommand = <
  TVariables extends { proposalId: string },
>(
  mutationFn: (variables: TVariables) => Promise<SolarLightReachDto>,
  scope: SolarLightReachCommandScope,
  options: { cacheTime?: number } = {},
): UseMutationResult<SolarLightReachDto, unknown, TVariables> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn,
    ...options,
    onSuccess: (read, { proposalId }) => {
      applySolarLightReachRead(queryClient, proposalId, read, scope)
    },
    onError: (_error, { proposalId }) => {
      invalidateSolarLightReachDependents(queryClient, proposalId, scope)
    },
  })
}
